import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import MainLayout from '../components/MainLayout';
import {
  Layers, Coins, Calendar, Plus, Trash2, Edit2, Loader2,
  Download, Upload, Filter, Save,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useActiveCompany } from '../hooks/useActiveCompany';
import { formatApiError } from '../lib/errorUtils';

const API = process.env.REACT_APP_BACKEND_URL + '/api/wage-master';
const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const TABS = [
  { id: 'wage-rates', label: 'Wage Rates', icon: Coins },
  { id: 'skill-categories', label: 'Skill Categories', icon: Layers },
  { id: 'pay-days', label: 'Monthly Pay Days', icon: Calendar },
];

const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

export default function WageMaster() {
  const [tab, setTab] = useState('wage-rates');
  const [activeCompany] = useActiveCompany();

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="wage-master-page">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            <Coins className="inline-block w-8 h-8 mr-2 mb-1 text-amber-700" />
            Wage Master
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Skill-category × Wage-act × Location → ₹/day · powers the O&M / Labour payroll method.
          </p>
        </div>

        {!activeCompany ? (
          <div className="bg-white border border-amber-300 rounded-lg p-6 text-amber-800 text-sm">
            Select a company from the top header to manage Wage Master.
          </div>
        ) : (
          <>
            <div className="flex space-x-2 border-b border-slate-200">
              {TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    data-testid={`wm-tab-${t.id}`}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      tab === t.id
                        ? 'text-[#0052FF] border-[#0052FF]'
                        : 'text-slate-600 border-transparent hover:text-slate-900'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {t.label}
                  </button>
                );
              })}
            </div>

            {tab === 'wage-rates' && <WageRatesTab companyId={activeCompany} />}
            {tab === 'skill-categories' && <SkillCategoriesTab companyId={activeCompany} />}
            {tab === 'pay-days' && <PayDaysTab companyId={activeCompany} />}
          </>
        )}
      </div>
    </MainLayout>
  );
}

// =============== Wage Rates Tab ===============

function WageRatesTab({ companyId }) {
  const [rows, setRows] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState({ skill: '', act: '', location: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/wage-rates?company_id=${companyId}`, { withCredentials: true }),
        axios.get(`${API}/skill-categories?company_id=${companyId}`, { withCredentials: true }),
      ]);
      setRows(r.data); setSkills(s.data);
    } catch (e) {
      toast.error(formatApiError(e) || 'Failed to load wage rates');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((r) =>
    (!filter.skill || r.skill_category === filter.skill) &&
    (!filter.act || (r.wage_act || '').toLowerCase().includes(filter.act.toLowerCase())) &&
    (!filter.location || (r.location || '').toLowerCase().includes(filter.location.toLowerCase()))
  ), [rows, filter]);

  const onSave = async (payload) => {
    try {
      if (editing?.id) {
        await axios.put(`${API}/wage-rates/${editing.id}`, payload, { withCredentials: true });
        toast.success('Wage rate updated');
      } else {
        await axios.post(`${API}/wage-rates`, { company_id: companyId, ...payload }, { withCredentials: true });
        toast.success('Wage rate added');
      }
      setShowForm(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e) || 'Save failed'); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this wage rate row?')) return;
    try {
      await axios.delete(`${API}/wage-rates/${id}`, { withCredentials: true });
      toast.success('Deleted'); load();
    } catch (e) { toast.error(formatApiError(e) || 'Delete failed'); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/wage-rates/template`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'wage_rate_master_template.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Template download failed'); }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append('file', f);
    try {
      const { data } = await axios.post(
        `${API}/wage-rates/bulk-upload?company_id=${companyId}`,
        fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (data.errors?.length) toast.warning(`Created ${data.created} · ${data.errors.length} error(s) — first: ${data.errors[0]?.error}`);
      else toast.success(`Created ${data.created} wage rate(s)`);
      load();
    } catch (err) { toast.error(formatApiError(err) || 'Upload failed'); }
    e.target.value = '';
  };

  return (
    <div className="space-y-4" data-testid="wm-wage-rates">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select value={filter.skill} onChange={(e) => setFilter({ ...filter, skill: e.target.value })}
            data-testid="wm-filter-skill"
            className="h-8 px-2 text-xs border border-slate-300 rounded">
            <option value="">All skills</option>
            {skills.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
          <input value={filter.act} onChange={(e) => setFilter({ ...filter, act: e.target.value })}
            placeholder="Act contains…" className="h-8 px-2 text-xs border border-slate-300 rounded w-44" />
          <input value={filter.location} onChange={(e) => setFilter({ ...filter, location: e.target.value })}
            placeholder="Location…" className="h-8 px-2 text-xs border border-slate-300 rounded w-32" />
          {(filter.skill || filter.act || filter.location) && (
            <button onClick={() => setFilter({ skill: '', act: '', location: '' })}
              className="text-[11px] text-slate-500 underline">clear</button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="wm-template-btn">
            <Download className="w-4 h-4 mr-1" /> Template
          </Button>
          <label className="inline-flex items-center gap-1 px-3 h-8 text-xs font-medium bg-violet-600 hover:bg-violet-700 text-white rounded cursor-pointer"
            data-testid="wm-upload-btn">
            <Upload className="w-4 h-4" /> Bulk Upload
            <input type="file" accept=".csv" onChange={onUpload} className="hidden" />
          </label>
          <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="wm-add-btn"
            className="bg-[#0052FF] hover:bg-[#0040CC]">
            <Plus className="w-4 h-4 mr-1" /> Add Rate
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="wm-rates-empty">
            <Coins className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm">{rows.length === 0 ? 'No wage rates yet.' : 'No rates match your filters.'}</p>
            <p className="text-xs text-slate-400 mt-1">
              {rows.length === 0 ? 'Add a row or download the CSV template to bulk-import.' : 'Try clearing filters.'}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600 uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Skill</th>
                <th className="px-3 py-2 text-left">Wage Act</th>
                <th className="px-3 py-2 text-left">Location</th>
                <th className="px-3 py-2 text-right">₹/day</th>
                <th className="px-3 py-2 text-right">₹/month</th>
                <th className="px-3 py-2 text-left">Effective</th>
                <th className="px-3 py-2 text-left">Notes</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`wm-rate-row-${r.id}`} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-800">{r.skill_category}</td>
                  <td className="px-3 py-2 text-slate-600">{r.wage_act}</td>
                  <td className="px-3 py-2 text-slate-600">{r.location || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-700 font-semibold">₹{fmt(r.daily_rate)}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-500">{r.monthly_rate ? `₹${fmt(r.monthly_rate)}` : '—'}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.effective_from}{r.effective_to ? ` → ${r.effective_to}` : ''}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 max-w-[200px] truncate" title={r.notes}>{r.notes || ''}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => { setEditing(r); setShowForm(true); }}
                      data-testid={`wm-rate-edit-${r.id}`}
                      className="p-1 text-slate-500 hover:text-[#0052FF]"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => onDelete(r.id)}
                      data-testid={`wm-rate-delete-${r.id}`}
                      className="p-1 text-slate-500 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <WageRateForm
          initial={editing}
          skills={skills}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function WageRateForm({ initial, skills, onClose, onSave }) {
  const [form, setForm] = useState({
    skill_category: initial?.skill_category || (skills[0]?.name || ''),
    wage_act: initial?.wage_act || '',
    location: initial?.location || '',
    daily_rate: initial?.daily_rate || '',
    monthly_rate: initial?.monthly_rate || '',
    effective_from: initial?.effective_from || new Date().toISOString().slice(0, 10),
    effective_to: initial?.effective_to || '',
    notes: initial?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.skill_category || !form.wage_act || !form.daily_rate) {
      return toast.error('Skill, Act and Daily Rate are required');
    }
    setSaving(true);
    await onSave({
      ...form,
      daily_rate: Number(form.daily_rate),
      monthly_rate: form.monthly_rate ? Number(form.monthly_rate) : null,
      effective_to: form.effective_to || null,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4">{initial ? 'Edit' : 'Add'} Wage Rate</h3>
        <div className="space-y-3">
          <Field label="Skill Category">
            <select value={form.skill_category}
              onChange={(e) => setForm({ ...form, skill_category: e.target.value })}
              data-testid="wm-rate-skill"
              className="w-full border rounded px-2 py-1.5 text-sm">
              {skills.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              {skills.length === 0 && <option value="">— Add categories first —</option>}
            </select>
          </Field>
          <Field label="Wage Act (free text)">
            <input value={form.wage_act}
              onChange={(e) => setForm({ ...form, wage_act: e.target.value })}
              placeholder="e.g. TN Industrial Establishment Act"
              list="wage-acts-form" data-testid="wm-rate-act"
              className="w-full border rounded px-2 py-1.5 text-sm" />
            <datalist id="wage-acts-form">
              <option value="Central Minimum Wages" />
              <option value="TN Industrial Establishment Act" />
              <option value="TN Shops and Establishment Act" />
              <option value="Karnataka Minimum Wages Act" />
              <option value="Kerala Minimum Wages Act" />
              <option value="Delhi Minimum Wages Act" />
            </datalist>
          </Field>
          <Field label="Location (optional)">
            <input value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="e.g. Chennai / PAN-India"
              data-testid="wm-rate-location"
              className="w-full border rounded px-2 py-1.5 text-sm" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Daily Rate ₹">
              <input type="number" min="0" step="0.01" value={form.daily_rate}
                onChange={(e) => setForm({ ...form, daily_rate: e.target.value })}
                placeholder="1008" data-testid="wm-rate-daily"
                className="w-full border rounded px-2 py-1.5 text-sm font-mono" />
            </Field>
            <Field label="Monthly Rate ₹ (informational)">
              <input type="number" min="0" step="0.01" value={form.monthly_rate}
                onChange={(e) => setForm({ ...form, monthly_rate: e.target.value })}
                placeholder="auto: 26 × daily" data-testid="wm-rate-monthly"
                className="w-full border rounded px-2 py-1.5 text-sm font-mono" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Effective From">
              <input type="date" value={form.effective_from}
                onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                data-testid="wm-rate-from"
                className="w-full border rounded px-2 py-1.5 text-sm" />
            </Field>
            <Field label="Effective To (optional)">
              <input type="date" value={form.effective_to}
                onChange={(e) => setForm({ ...form, effective_to: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm" />
            </Field>
          </div>
          <Field label="Notes">
            <input value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Tier 1 / approved by HR Head"
              className="w-full border rounded px-2 py-1.5 text-sm" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="wm-rate-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

// =============== Skill Categories Tab ===============

function SkillCategoriesTab({ companyId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/skill-categories?company_id=${companyId}`, { withCredentials: true });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e) || 'Failed to load skill categories');
    } finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newName.trim()) return toast.error('Name is required');
    try {
      await axios.post(`${API}/skill-categories`,
        { company_id: companyId, name: newName.trim(), description: newDesc },
        { withCredentials: true });
      toast.success('Added');
      setNewName(''); setNewDesc(''); load();
    } catch (e) { toast.error(formatApiError(e) || 'Add failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this skill category?')) return;
    try {
      await axios.delete(`${API}/skill-categories/${id}`, { withCredentials: true });
      toast.success('Deleted'); load();
    } catch (e) { toast.error(formatApiError(e) || 'Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="wm-skills">
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Add Skill Category</h3>
        <div className="flex gap-2">
          <input value={newName} onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Apprentice / Trainee"
            data-testid="wm-skill-name"
            className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm" />
          <input value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Description (optional)"
            className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm" />
          <Button onClick={add} data-testid="wm-skill-add" className="bg-[#0052FF] hover:bg-[#0040CC]">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Skill categories drive the Wage Rate lookup for O&M / Labour employees.
          Defaults (Highly Skilled / Skilled / Semi Skilled / Unskilled) are auto-seeded.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600 uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`wm-skill-row-${r.id}`} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{r.description}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => del(r.id)}
                      data-testid={`wm-skill-del-${r.id}`}
                      className="p-1 text-slate-500 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// =============== Monthly Pay Days Tab ===============

function PayDaysTab({ companyId }) {
  const [periods, setPeriods] = useState([]);
  const [groups, setGroups] = useState([]);
  const [periodId, setPeriodId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);



  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [pp, gg] = await Promise.all([
        axios.get(`${API_BASE}/payroll/periods`, { withCredentials: true }),
        axios.get(`${API_BASE}/controls/ctc-groups?company_id=${companyId}`, { withCredentials: true }),
      ]);
      setPeriods(pp.data || []);
      setGroups(gg.data || []);
      setPeriodId((prev) => prev || ((pp.data || [])[0]?.id || ''));
    } catch (e) {
      toast.error(formatApiError(e) || 'Failed to load periods/groups');
    } finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const loadRows = useCallback(async () => {
    if (!periodId) { setRows([]); return; }
    try {
      const { data } = await axios.get(
        `${API}/pay-days?company_id=${companyId}&period_id=${periodId}`, { withCredentials: true });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e) || 'Failed to load pay days'); }
  }, [companyId, periodId]);

  useEffect(() => { loadRows(); }, [loadRows]);

  const onSave = async (payload) => {
    try {
      await axios.post(`${API}/pay-days`, { company_id: companyId, period_id: periodId, ...payload },
        { withCredentials: true });
      toast.success('Pay days saved');
      setShowForm(false); loadRows();
    } catch (e) { toast.error(formatApiError(e) || 'Save failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Remove this pay-days override?')) return;
    try {
      await axios.delete(`${API}/pay-days/${id}`, { withCredentials: true });
      toast.success('Removed'); loadRows();
    } catch (e) { toast.error(formatApiError(e) || 'Delete failed'); }
  };

  // Build distinct parent groups list for the form.
  const parentGroups = useMemo(() => Array.from(new Set(
    (groups || []).map((g) => g.parent_group).filter(Boolean)
  )).sort(), [groups]);

  return (
    <div className="space-y-4" data-testid="wm-pay-days">
      {/* Stage 73.10 — Clarify optional/override nature of this tab so admins
          stop treating it as a mandatory input. Pay days now auto-resolve
          from each CTC group's base_days_fixed + the cycle definition. */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-[11px] text-emerald-900 flex items-start gap-2" data-testid="wm-pd-info-banner">
        <span className="font-semibold shrink-0">ℹ️ Optional:</span>
        <span>
          Pay days <b>auto-resolve</b> from each CTC group's <b>base_days_fixed</b> setting (Business Controls → CTC Groups) and the period's cycle (e.g. 21st → 20th).
          Use this tab <b>only when you need a one-off override</b> for a specific period — e.g. a festival shutdown, leap-month adjustment, or a group-specific tweak.
          For routine months, you can skip this entirely.
        </span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600">Period:</label>
          <select value={periodId} onChange={(e) => setPeriodId(e.target.value)}
            data-testid="wm-pd-period"
            className="h-9 px-3 text-sm border border-slate-300 rounded">
            <option value="">— select period —</option>
            {periods.map((p) => <option key={p.id} value={p.id}>{`${p.period_name} (${p.status})`}</option>)}
          </select>
        </div>
        <Button onClick={() => setShowForm(true)} disabled={!periodId} data-testid="wm-pd-add-btn"
          className="bg-[#0052FF] hover:bg-[#0040CC]">
          <Plus className="w-4 h-4 mr-1" /> Add Override
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : !periodId ? (
          <div className="p-10 text-center text-slate-400 text-sm">Select a period above to view / set pay-days overrides.</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="wm-pd-empty">
            <Calendar className="w-10 h-10 mx-auto text-emerald-400 mb-2" />
            <p className="text-sm font-semibold text-emerald-700">✅ No override needed — payroll runs with auto pay days</p>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
              Pay days auto-resolve per employee from <b>their CTC group's base_days_fixed</b> (set in Business Controls → CTC Groups).
              Different groups can have different defaults — flexible per group.
            </p>
            <p className="text-[11px] text-slate-400 mt-2 max-w-md mx-auto">
              Add an override here ONLY when you need a one-off change for this period (e.g. festival month, partial month closure).
              Skip this tab entirely for routine months — the payroll engine handles it from the cycle + group config.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600 uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Scope</th>
                <th className="px-3 py-2 text-left">Group / Parent</th>
                <th className="px-3 py-2 text-right">Pay Days</th>
                <th className="px-3 py-2 text-left">Reason</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const grp = groups.find((g) => g.id === r.ctc_group_id);
                return (
                  <tr key={r.id} data-testid={`wm-pd-row-${r.id}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      {r.ctc_group_id ? (
                        <span className="text-[10px] bg-violet-100 text-violet-700 px-2 py-0.5 rounded font-semibold">CTC GROUP</span>
                      ) : (
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-semibold">PARENT GROUP</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-medium">
                      {grp ? `${grp.code} — ${grp.name}` : (r.parent_group || '—')}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700 font-bold">{r.pay_days}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.override_reason}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => del(r.id)}
                        data-testid={`wm-pd-del-${r.id}`}
                        className="p-1 text-slate-500 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <PayDaysForm
          parentGroups={parentGroups}
          groups={groups}
          onClose={() => setShowForm(false)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function PayDaysForm({ parentGroups, groups, onClose, onSave }) {
  const [scope, setScope] = useState('parent'); // 'parent' | 'group'
  const [parent, setParent] = useState(parentGroups[0] || '');
  const [groupId, setGroupId] = useState('');
  const [days, setDays] = useState(26);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (scope === 'parent' && !parent) return toast.error('Select a parent group');
    if (scope === 'group' && !groupId) return toast.error('Select a CTC group');
    if (!days || days < 1 || days > 31) return toast.error('Pay days must be 1..31');
    setSaving(true);
    await onSave({
      ctc_group_id: scope === 'group' ? groupId : null,
      parent_group: scope === 'parent' ? parent : null,
      pay_days: Number(days),
      override_reason: reason,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4">Set Monthly Pay Days</h3>
        <div className="space-y-3">
          <div className="flex gap-2">
            <button onClick={() => setScope('parent')}
              data-testid="wm-pd-scope-parent"
              className={`flex-1 px-3 py-2 text-sm rounded border ${scope === 'parent'
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white border-slate-300 text-slate-600'}`}>
              Parent Group (e.g. all of O&M)
            </button>
            <button onClick={() => setScope('group')}
              data-testid="wm-pd-scope-group"
              className={`flex-1 px-3 py-2 text-sm rounded border ${scope === 'group'
                ? 'bg-violet-600 text-white border-violet-600'
                : 'bg-white border-slate-300 text-slate-600'}`}>
              Specific CTC Group
            </button>
          </div>
          {scope === 'parent' ? (
            <Field label="Parent Group">
              <select value={parent} onChange={(e) => setParent(e.target.value)}
                data-testid="wm-pd-parent"
                className="w-full border rounded px-2 py-1.5 text-sm">
                {parentGroups.length === 0 && <option value="">— No parent groups defined yet —</option>}
                {parentGroups.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          ) : (
            <Field label="CTC Group">
              <select value={groupId} onChange={(e) => setGroupId(e.target.value)}
                data-testid="wm-pd-group"
                className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">— select —</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{`${g.code} — ${g.name}`}</option>)}
              </select>
            </Field>
          )}
          <Field label="Pay Days">
            <input type="number" min="1" max="31" value={days}
              onChange={(e) => setDays(e.target.value)}
              data-testid="wm-pd-days"
              className="w-full border rounded px-2 py-1.5 text-sm font-mono" />
          </Field>
          <Field label="Reason">
            <input value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Standard 26 days for blue-collar"
              data-testid="wm-pd-reason"
              className="w-full border rounded px-2 py-1.5 text-sm" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="wm-pd-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-slate-700 mb-1">{label}</label>
      {children}
    </div>
  );
}
